import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FileUp, X, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatFileSize } from '../companies.logic'
import { ALLOWED_FILE_TYPES } from '../constants'

interface EvidenceUploadFieldProps {
  label: string
  hint?: string
  value: File | null
  onChange: (file: File | null) => void
  error?: string
  required?: boolean
}

export function EvidenceUploadField({
  label,
  hint,
  value,
  onChange,
  error,
  required,
}: EvidenceUploadFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    if (file) onChange(file)
    e.target.value = ''
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <span className="text-foreground/70 text-[10px] font-bold tracking-widest uppercase">
          {label}
        </span>
        {required && <span className="text-destructive text-[10px]">*</span>}
      </div>

      {hint && <p className="text-muted-foreground text-[10px]">{hint}</p>}

      {value ? (
        <div className="bg-muted/30 flex items-center gap-2 rounded-lg border px-3 py-2">
          <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{value.name}</p>
            <p className="text-muted-foreground text-[10px]">{formatFileSize(value.size)}</p>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={() => {
              onChange(null)
            }}
            aria-label={t('actions.clear')}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`text-muted-foreground hover:border-primary/50 hover:bg-muted/30 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 text-xs transition-colors ${
            error ? 'border-destructive' : 'border-border'
          }`}
        >
          <FileUp className="h-4 w-4" />
          {t('companies.selectFile')}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ALLOWED_FILE_TYPES.join(',')}
        onChange={handleChange}
      />

      {error && <p className="text-destructive text-[10px]">{error}</p>}
    </div>
  )
}
